import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import MembershipPlanCard from '@/components/MembershipPlanCard';
import PSText from '@/components/ui/PSText';
import {
  ACCESS_PASS_PLAN,
  LONG_COPY_PLAN,
  PREMIUM_PLAN,
  TRIAL_PLAN,
} from '@/src/lib/membershipFixtures';

/**
 * ── /components/membership-plan-card ─────────────────────────────────────
 *
 * MembershipPlanCard's real prop surface (plan, onPress,
 * withFeatures) as its call sites actually exercise it —
 * MembershipUpsellSection (home + the Access Pass page's upsell) and
 * memberships/index.tsx's "All plans" comparison list.
 *
 * White text drawn only for home's `$brandDark` Access Pass block, so every
 * block below wraps it in that surface — on the near-white gallery page the
 * copy would otherwise read as a rendering bug.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function MembershipPlanCardPreview() {
  return (
    <>
      <Head>
        <title>MembershipPlanCard · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* kind — the card's look keys off `plan.kind` (KIND_LOOK in
            components/MembershipPlanCard.tsx): its own glow, price caption,
            and, for trial, the corner badge. */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$lg" rounded="$base">
            <PSText.Body3 color="$textWhiteMuted">
              kind — trial (pink glow, TRIAL badge, one-time caption) vs
              access_pass (wine glow, monthly caption)
            </PSText.Body3>
            <MembershipPlanCard
              plan={TRIAL_PLAN}
              onPress={() => {}}
              ctaLabel="Buy now"
              withFeatures
            />
            <MembershipPlanCard
              plan={ACCESS_PASS_PLAN}
              onPress={() => {}}
              ctaLabel="Buy now"
              withFeatures
            />
          </YStack>

          {/* withFeatures — off by default (home's upsell never passes
            it: that list pitches, they don't compare), on
            for memberships/index.tsx's "All plans" section, the one list
            written to be compared plan-for-plan. `limitLabel` inside the
            component reads the wire backwards from how it looks: `null` is
            unlimited, `0` is "not included" — never "0 left". ACCESS_PASS_PLAN
            mixes both (two unlimited perks, a capped guest-pass count);
            TRIAL_PLAN is mostly zeroes. PREMIUM_PLAN ships an EMPTY perk list
            on staging today — `withFeatures` true still renders nothing below
            the price, because the component guards on
            `plan.planFeatures.length > 0`, so it's visually identical to
            `withFeatures={false}` even though the prop says otherwise. */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$lg" rounded="$base">
            <Text fontFamily="$body" fontSize="$small" color="$textWhiteMuted">
              withFeatures — the perk rows, from the real plan shapes: mixed
              unlimited/limited, mostly-blocked, and an empty list that renders
              nothing despite the prop being on
            </Text>
            <MembershipPlanCard
              plan={ACCESS_PASS_PLAN}
              onPress={() => {}}
              ctaLabel="Buy now"
              withFeatures
            />
            <MembershipPlanCard
              plan={TRIAL_PLAN}
              onPress={() => {}}
              ctaLabel="Buy now"
              withFeatures
            />
            <MembershipPlanCard
              plan={PREMIUM_PLAN}
              onPress={() => {}}
              ctaLabel="Buy now"
              withFeatures
            />
          </YStack>

          {/* Long copy / an unseeded tier. Despite membershipFixtures's own
            docstring on LONG_COPY_PLAN ("carrying copy long enough to hit
            the card's clamps"), the card has no clamp to hit today: the name
            (PSText.H3) is commented out of the JSX entirely, and the
            description (PSText.Body2) carries no `numberOfLines`. So this is
            just the honest current behaviour — the description wraps to
            however many lines it needs and the card grows to fit, rather
            than truncating. Worth watching on web especially if a clamp is
            ever added back. */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$lg" rounded="$base">
            <Text fontFamily="$body" fontSize="$small" color="$textWhiteMuted">
              long description, unseeded tier — no clamp exists on this card
              today (name is commented out, description has no numberOfLines),
              so it just wraps and the card grows taller
            </Text>
            <MembershipPlanCard
              plan={LONG_COPY_PLAN}
              onPress={() => {}}
              ctaLabel="Buy now"
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
